import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, 
  Book, 
  Database, 
  Beaker,
  Loader,
  Library,
  CalendarDays,
  FlaskConical,
  AlertCircle,
  Clock,
  CheckCircle2
} from 'lucide-react';

// Study Design status line for the configuration landing (FECOA-1600/1601).
const DESIGN_STATUS_BANNER = {
  draft: { text: 'Draft — design has not yet been submitted for approval', cls: 'bg-gray-50 border-gray-200 text-gray-700', Icon: AlertCircle },
  in_review: { text: 'Pending Review — locked from editing until an admin decides', cls: 'bg-blue-50 border-blue-200 text-blue-800', Icon: Clock },
  denied: { text: 'Needs Revision — returned by the reviewer; address the feedback and resubmit', cls: 'bg-red-50 border-red-200 text-red-800', Icon: AlertCircle },
  approved: { text: 'Approved — ready to publish', cls: 'bg-teal-50 border-teal-200 text-teal-800', Icon: CheckCircle2 },
  published: { text: 'Published', cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', Icon: CheckCircle2 },
};
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';
import ApprovalSettingsControl from '../AssessmentBuilder/ApprovalSettingsControl';

const isInternalUser = () => {
  try {
    return !!JSON.parse(localStorage.getItem('user') || '{}')?.is_internal;
  } catch {
    return false;
  }
};

const StudyBuilderOptions = () => {
  const navigate = useNavigate();
  const { studyName } = useParams();
  const [loading, setLoading] = useState(true);
  const [studyData, setStudyData] = useState(null);

  // Ensure draft ID is set when this component mounts
  useEffect(() => {
    const fetchStudyDraft = async () => {
      setLoading(true);
      try {
        // First check if we already have the draft ID
        let draftId = localStorage.getItem('currentStudyDraftId');
        
        // If not, fetch it based on study name
        if (!draftId && studyName) {
          const draft = await studyBuilderService.getStudyDraftByName(studyName);
          if (draft && draft.id) {
            draftId = draft.id.toString();
            localStorage.setItem('currentStudyDraftId', draftId);
            setStudyData(draft);
            
            if (draft.study) {
              localStorage.setItem('currentStudyId', draft.study.toString());
            }
          }
        } else if (studyName) {
          // If we have the ID but not the study data, fetch the data
          const draft = await studyBuilderService.getStudyDraftByName(studyName);
          setStudyData(draft);
        }
      } catch (error) {
        console.error('Error fetching study draft:', error);
        toast.error('Failed to load study draft data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStudyDraft();
  }, [studyName]);

  const handleNavigateToSchedule = async () => {
    try {
      // Ensure we have the draft ID before navigating
      const draftId = localStorage.getItem('currentStudyDraftId');
      
      if (!draftId) {
        // If no draft ID, fetch it first
        const draft = await studyBuilderService.getStudyDraftByName(studyName);
        if (draft && draft.id) {
          localStorage.setItem('currentStudyDraftId', draft.id.toString());
          if (draft.study) {
            localStorage.setItem('currentStudyId', draft.study.toString());
          }
        } else {
          throw new Error('Could not find study draft');
        }
      }
      
      // Now navigate to schedule builder
      navigate(`/studybuilder/${studyName}/schedule`);
    } catch (error) {
      console.error('Error navigating to schedule builder:', error);
      toast.error('Failed to load study design page');
    }
  };

  const formatStudyName = (name) => {
    if (!name) return '';
    try {
      return decodeURIComponent(name);
    } catch (e) {
      return name;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link
            to="/studybuilder"
            className="group flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1 group-hover:transform group-hover:-translate-x-1 transition-transform duration-200" />
            <span>Back to Study Builder</span>
          </Link>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 bg-primary-600 flex items-center justify-between gap-4">
            <div className="flex items-center">
              <Beaker className="w-6 h-6 text-white mr-3" />
              <h2 className="text-xl font-semibold text-white">Study Configuration</h2>
            </div>
            {isInternalUser() && studyData?.study && (
              <ApprovalSettingsControl studyId={studyData.study} />
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="px-6 py-16 flex flex-col items-center justify-center">
              <Loader className="w-10 h-10 text-primary-600 animate-spin mb-4" />
              <p className="text-gray-500">Loading study information...</p>
            </div>
          ) : (
            <div className="p-8">
              {/* Study Title */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 text-primary-600 rounded-full mb-4">
                  <FlaskConical className="w-8 h-8" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {formatStudyName(studyName)}
                </h1>
                <p className="text-gray-600 max-w-md mx-auto">
                  Choose an option below to configure your study's library or design settings
                </p>
                <div className="h-1 w-20 bg-primary-600 mx-auto mt-4"></div>
              </div>

              {/* Study Design status banner (FECOA-1600/1601) */}
              {(() => {
                const meta = DESIGN_STATUS_BANNER[studyData?.review_status || 'draft'];
                if (!meta) return null;
                const Icon = meta.Icon;
                return (
                  <div className={`max-w-3xl mx-auto mb-6 flex items-center gap-2 px-4 py-3 rounded-lg border text-sm ${meta.cls}`}>
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span><span className="font-semibold">Study Design:</span> {meta.text}</span>
                  </div>
                );
              })()}

              {/* Option Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {/* Assessment Builder Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md dark:hover:shadow-gray-900/20 transition-shadow duration-300">
                  <div className="flex flex-col h-full items-center text-center">
                    <div className="bg-primary-50 text-primary-600 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                      <Library className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Assessment Builder</h3>
                    <p className="text-gray-600 mb-6 flex-grow">
                      Create and manage assessments with custom screens and questions
                    </p>
                    <button
                      // Scope the builder to this study's assessments when the draft is
                      // linked to a study; otherwise fall back to the full list (FECOA-1826)
                      onClick={() => navigate(studyData?.study ? `/assessment-builder?study=${studyData.study}` : '/assessment-builder')}
                      className="w-full py-3 bg-white dark:bg-gray-800 text-primary-600 font-medium rounded-lg border border-primary-200 hover:bg-primary-50 transition-colors duration-300 flex items-center justify-center"
                    >
                      <Book className="w-5 h-5 mr-2" />
                      Open Builder
                    </button>
                  </div>
                </div>

                {/* Study Design Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md dark:hover:shadow-gray-900/20 transition-shadow duration-300">
                  <div className="flex flex-col h-full items-center text-center">
                    <div className="bg-primary-50 text-primary-600 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                      <CalendarDays className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Study Design</h3>
                    <p className="text-gray-600 mb-6 flex-grow">
                      Define schedule, arms, stages, and other elements of your study structure
                    </p>
                    <button
                      onClick={handleNavigateToSchedule}
                      className="w-full py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors duration-300 flex items-center justify-center"
                    >
                      <Database className="w-5 h-5 mr-2" />
                      Configure Design
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudyBuilderOptions;